import React, { useState, useRef } from 'react';
import { _T, C, CSS } from '../../shared/theme';
import { s_TI } from '../../shared/styles';
import { createBusiness } from './businessApi';
import { INDUSTRIES } from './BusinessDashboard';

const BASE = process.env.REACT_APP_API_URL || '/api';

const DAYS = [
  { key: 'mon', nl: 'Maandag',   en: 'Monday' },
  { key: 'tue', nl: 'Dinsdag',   en: 'Tuesday' },
  { key: 'wed', nl: 'Woensdag',  en: 'Wednesday' },
  { key: 'thu', nl: 'Donderdag', en: 'Thursday' },
  { key: 'fri', nl: 'Vrijdag',   en: 'Friday' },
  { key: 'sat', nl: 'Zaterdag',  en: 'Saturday' },
  { key: 'sun', nl: 'Zondag',    en: 'Sunday' },
];

export default function BusinessSetupScreen({ lang, currentUserId, onCreated }) {
  const nl = lang === 'nl';
  const [name, setName] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [logoPreview, setLogoPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [industry, setIndustry] = useState('other');
  const [address, setAddress] = useState('');
  const [description, setDescription] = useState('');
  const [hours, setHours] = useState({ mon: '09:00-17:00', tue: '09:00-17:00', wed: '09:00-17:00', thu: '09:00-17:00', fri: '09:00-17:00', sat: '', sun: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const fileRef = useRef();

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadError(null);

    // Local preview
    const reader = new FileReader();
    reader.onload = ev => setLogoPreview(ev.target.result);
    reader.readAsDataURL(file);

    // Upload to server
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${BASE}/upload`, { method: 'POST', body: fd });
      const data = await res.json();
      if (data.url) {
        setLogoUrl(data.url);
      } else {
        setUploadError(data.error || (nl ? 'Upload mislukt.' : 'Upload failed.'));
        setLogoPreview(null);
      }
    } catch {
      setUploadError(nl ? 'Verbindingsfout bij upload.' : 'Upload connection error.');
      setLogoPreview(null);
    }
    setUploading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { setError(nl ? 'Bedrijfsnaam is verplicht.' : 'Business name is required.'); return; }
    setLoading(true);
    setError(null);
    const data = await createBusiness({ owner_id: currentUserId, name: name.trim(), logo_url: logoUrl || null, industry: industry || 'other', address: address.trim() || null, description: description.trim() || null, opening_hours: hours });
    if (data.id) {
      onCreated(data);
    } else {
      setError(data.error || (nl ? 'Iets ging mis.' : 'Something went wrong.'));
      setLoading(false);
    }
  };

  return (
    <div style={{ padding: '24px 16px', maxWidth: 480, margin: '0 auto' }}>
      <style>{CSS}</style>
      <div style={{ fontSize: 9, color: _T.accent, letterSpacing: 3, fontWeight: 700, marginBottom: 4 }}>BEDRIJF</div>
      <div style={{ fontFamily: 'Georgia,serif', fontSize: 22, color: C.white, marginBottom: 6 }}>
        {nl ? 'Maak jouw bedrijf aan' : 'Create your business'}
      </div>
      <div style={{ color: C.silver, fontSize: 13, marginBottom: 24, lineHeight: 1.6 }}>
        {nl ? 'Stel je bedrijfsprofiel in voor jouw team.' : 'Set up your business profile for your team.'}
      </div>

      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div>
          <div style={{ color: C.silver, fontSize: 11, marginBottom: 6 }}>{nl ? 'Bedrijfsnaam *' : 'Business name *'}</div>
          <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder={nl ? 'Jouw bedrijfsnaam' : 'Your business name'} style={s_TI({ width: '100%', boxSizing: 'border-box' })} />
        </div>

        {/* Logo upload */}
        <div>
          <div style={{ color: C.silver, fontSize: 11, marginBottom: 8 }}>{nl ? 'Logo (optioneel)' : 'Logo (optional)'}</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {/* Preview or placeholder */}
            <div
              onClick={() => fileRef.current?.click()}
              style={{
                width: 64, height: 64, borderRadius: 14,
                background: logoPreview ? 'transparent' : _T.bg4,
                border: `2px dashed ${uploading ? _T.accent : (logoPreview ? _T.accent : _T.cardBorderBot)}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', overflow: 'hidden', flexShrink: 0, position: 'relative',
              }}
            >
              {logoPreview
                ? <img src={logoPreview} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                : <span style={{ fontSize: 24, opacity: 0.4 }}>🏢</span>
              }
              {uploading && (
                <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <span style={{ fontSize: 18 }}>⏳</span>
                </div>
              )}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
                style={{ width: '100%', background: `${_T.accent}15`, border: `1px solid ${_T.accent}44`, borderRadius: 10, color: _T.accent, fontSize: 13, fontWeight: 600, padding: '9px 14px', cursor: uploading ? 'default' : 'pointer' }}>
                {uploading ? (nl ? 'Uploaden...' : 'Uploading...') : (logoPreview ? (nl ? 'Andere afbeelding' : 'Change image') : (nl ? 'Afbeelding kiezen' : 'Choose image'))}
              </button>
              <div style={{ color: C.silver, fontSize: 10, marginTop: 4 }}>{nl ? 'JPG, PNG, WEBP – max 5 MB' : 'JPG, PNG, WEBP – max 5 MB'}</div>
            </div>
          </div>
          {uploadError && <div style={{ color: C.red, fontSize: 11, marginTop: 6 }}>⚠ {uploadError}</div>}
          <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/gif,image/webp" onChange={handleFileChange} style={{ display: 'none' }} />
        </div>

        {/* Industry */}
        <div>
          <div style={{ color: C.silver, fontSize: 11, marginBottom: 8 }}>{nl ? 'Sector *' : 'Industry *'}</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 6 }}>
            {INDUSTRIES.map(ind => (
              <button key={ind.id} type="button" onClick={() => setIndustry(ind.id)}
                style={{ background: industry === ind.id ? `${_T.accent}20` : _T.bg4, border: `1.5px solid ${industry === ind.id ? _T.accent : _T.cardBorderBot}`, borderRadius: 12, padding: '9px 6px', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, transition: 'all 0.15s' }}>
                <span style={{ fontSize: 18 }}>{ind.icon}</span>
                <span style={{ fontSize: 9, color: industry === ind.id ? _T.accent : C.silver, fontWeight: industry === ind.id ? 700 : 400, textAlign: 'center', lineHeight: 1.2 }}>{nl ? ind.nl : ind.en}</span>
              </button>
            ))}
          </div>
        </div>

        <div>
          <div style={{ color: C.silver, fontSize: 11, marginBottom: 6 }}>{nl ? 'Adres (optioneel)' : 'Address (optional)'}</div>
          <input type="text" value={address} onChange={e => setAddress(e.target.value)} placeholder={nl ? 'Straat, Stad' : 'Street, City'} style={s_TI({ width: '100%', boxSizing: 'border-box' })} />
        </div>
        <div>
          <div style={{ color: C.silver, fontSize: 11, marginBottom: 6 }}>{nl ? 'Beschrijving (optioneel)' : 'Description (optional)'}</div>
          <textarea value={description} onChange={e => setDescription(e.target.value)} placeholder={nl ? 'Beschrijf je bedrijf...' : 'Describe your business...'} rows={3}
            style={{ ...s_TI({ width: '100%', boxSizing: 'border-box' }), resize: 'vertical', fontFamily: 'inherit' }} />
        </div>

        <div style={{ background: _T.bg3, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 14, padding: '14px' }}>
          <div style={{ color: C.silver, fontSize: 11, marginBottom: 10, fontWeight: 600 }}>{nl ? 'Openingstijden' : 'Opening hours'}</div>
          {DAYS.map(d => (
            <div key={d.key} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
              <div style={{ color: C.silver, fontSize: 12, width: 80, flexShrink: 0 }}>{nl ? d.nl : d.en}</div>
              <input type="text" value={hours[d.key]} onChange={e => setHours(prev => ({ ...prev, [d.key]: e.target.value }))}
                placeholder={nl ? 'bijv. 09:00-17:00' : 'e.g. 09:00-17:00'}
                style={{ ...s_TI({ flex: 1 }), fontSize: 12, padding: '7px 10px' }} />
            </div>
          ))}
        </div>

        {error && <div style={{ color: C.red, fontSize: 12, textAlign: 'center' }}>⚠ {error}</div>}

        <button type="submit" disabled={loading || uploading}
          style={{ background: (loading || uploading) ? `${_T.accent}66` : `linear-gradient(135deg,${_T.grad1},${_T.grad2})`, border: 'none', borderRadius: 14, color: '#0a1220', fontSize: 14, fontWeight: 700, padding: '14px', cursor: (loading || uploading) ? 'default' : 'pointer', marginTop: 4 }}>
          {loading ? (nl ? 'Aanmaken...' : 'Creating...') : (nl ? 'Bedrijf aanmaken →' : 'Create business →')}
        </button>
      </form>
    </div>
  );
}
